import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import Dropdown from "ui/components/Dropdown/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import CardSelect from "ui/components/CardSelect/CardSelect";

import { TenantEditCardProps } from "./types";
import { useHandleTextFieldChange, useHandleDropdownChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { TENANT_EDIT_FORM_SCHEMA } from "./validation";
import { validateYup } from "services/forms/validation/validation";
import { getTenantAPI, updateTenantAPI } from "services/api/tenants/api";
import { initialTenantEditForm, timezones } from "services/api/tenants/consts";
import { TenantEditFormValues, TenantAddress } from "services/api/tenants/types";
import { transformTenantEditFormValues } from "services/api/tenants/transformation";
import { getApiErrorByCode } from "services/api/transformation";
import {
  AddressEditModal,
  AddressEditFormValues,
  initialAddressEditFormValues,
} from "ui/components/Map/AddressEditModal";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import OibDropdown from "ui/components/Dropdown/OibDropdown/OibDropdown";
import { SudRegResponse } from "services/api/contacts/types";
import { getTenantBankAccountsAPI, deleteTenantBankAccountAPI } from "services/api/tenants/api";
import { TenantBankAccount } from "services/api/tenants/types";
import TenantBankAccountEditCard from "../TenantBankAccountEditCard/TenantBankAccountEditCard";
import { TableSearch } from "ui/components/Table/TableSearch";
import { TENANT_BANK_ACCOUNT_COLUMNS } from "../tenantInfo/consts";
import { Dialog } from "ui/components/Dialog";
import PhoneTextField from "ui/components/TextField/PhoneTextField/PhoneTextField";
import { MobilePhoneTextField } from "ui/components/TextField/MobilePhoneTextField";
import { EmailTextField } from "ui/components/TextField/EmailTextField";
import { WebTextField } from "ui/components/TextField/WebTextField";


const TenantEditCard: React.FC<TenantEditCardProps> = (props) => {
  const { tenantId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<TenantEditFormValues>(initialTenantEditForm);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [logoUpload, setLogoUpload] = useState<FleetDocumentUpload[]>([]);
  const [bankAccounts, setBankAccounts] = useState<TenantBankAccount[]>([]);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [editBankAccountId, setEditBankAccountId] = useState<string | null>(null);
  const [deleteBankAccountId, setDeleteBankAccountId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenant = async (id: string) => {
      if (id === undefined) {
        setFormValues(initialTenantEditForm);
        setLogoUpload([]);
        return;
      }

      setIsLoading(true);

      try {
        const result = await getTenantAPI(id);
        if (result) {
          setFormValues(await transformTenantEditFormValues(result));
          if (result.logoUpload) {
            setLogoUpload([result.logoUpload]);
          } else {
            setLogoUpload([]);
          }
          setBankAccounts(result.bankAccounts ?? []);
        }
      } catch {
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      setErrors({});
    };

    if (show && tenantId) {
      fetchTenant(tenantId);
    }
  }, [show, tenantId]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, TENANT_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      if (formValues.id) {
        await updateTenantAPI(formValues.id, formValues, logoUpload);
        if (onSuccess) onSuccess();
      }
    } catch (err: any) {
      setIsLoading(false);
      const oibError = getApiErrorByCode(err, "TenantOibExists");
      if (oibError) {
        setErrors((prev) => ({ ...prev, personalIdentificationNumber: oibError }));
      }
      return;
    }

    setIsLoading(false);
  };

  const handleLogoUploadSave = useCallback(
    (uploads: FleetDocumentUpload[]) => {
      setLogoUpload(uploads.length > 0 ? [uploads[0]] : []);
    },
    []
  );

  const handleLogoUploadDelete = useCallback((_id: string) => {
    setLogoUpload([]);
  }, []);

  const fetchBankAccounts = useCallback(async () => {
    if (!tenantId) return;
    try {
      const result = await getTenantBankAccountsAPI(tenantId);
      setBankAccounts(result);
    } catch {
      setBankAccounts([]);
    }
  }, [tenantId]);

  const handleBankAccountEdit = (id: string) => {
    setEditBankAccountId(id);
    setShowBankAccountModal(true);
  };

  // Klik na koš ne smije otvoriti uređivanje reda ispod sebe.
  const handleBankAccountDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteBankAccountId(id);
    },
    []
  );

  const handleBankAccountAddNew = () => {
    setEditBankAccountId(null);
    setShowBankAccountModal(true);
  };

  const handleBankAccountEditSuccess = () => {
    setShowBankAccountModal(false);
    setEditBankAccountId(null);
    fetchBankAccounts();
  };

  const handleBankAccountDeleteConfirm = useCallback(async () => {
    if (!tenantId || !deleteBankAccountId) return;
    await deleteTenantBankAccountAPI(tenantId, deleteBankAccountId);
    setDeleteBankAccountId(null);
    fetchBankAccounts();
  }, [tenantId, deleteBankAccountId, fetchBankAccounts]);

  const handleTenantTypeChange = useCallback(
    (id: string) => {
      setFormValues((prev) => ({
        ...prev,
        tenantTypeId: id,
      }));
    },
    [setFormValues]
  );

  const timezoneOptions: DropdownOption[] = timezones.map((tz) => ({
    value: tz.id,
    text: tz.name,
  }));

  const isCompany = formValues.tenantTypeId === "C";
  const isPerson = formValues.tenantTypeId === "P";

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressValues, setAddressValues] = useState<AddressEditFormValues>(
    initialAddressEditFormValues
  );

  const handleOpenAddressModal = useCallback(() => {
    setAddressValues(
      formValues.address
        ? {
            country: formValues.address.country,
            state: formValues.address.state,
            city: formValues.address.city,
            postalCode: formValues.address.postalCode,
            line1: formValues.address.line1,
            line2: formValues.address.line2,
            latitude: formValues.address.latitude,
            longitude: formValues.address.longitude,
          }
        : initialAddressEditFormValues
    );
    setShowAddressModal(true);
  }, [formValues.address]);

  const handleCloseAddressModal = useCallback(() => {
    setShowAddressModal(false);
  }, []);

  const handleAddressChange = useCallback((values: AddressEditFormValues) => {
    setAddressValues(values);
  }, []);

  const handleOibSelect = useCallback(
    (oib: string, sudRegData: SudRegResponse) => {
      const sudRegAddress = sudRegData.address;
      const composedAddress = sudRegAddress
        ? [
            sudRegAddress.line1,
            sudRegAddress.postalCode,
            sudRegAddress.city,
            sudRegAddress.state,
            sudRegAddress.country,
          ]
            .filter(Boolean)
            .join(", ")
        : null;

      setFormValues((prev) => ({
        ...prev,
        personalIdentificationNumber: oib,
        companyName: sudRegData.name ?? prev.companyName,
        email: sudRegData.emails?.[0] ?? prev.email,
        address: sudRegAddress
          ? {
              id: prev.address?.id ?? null,
              city: sudRegAddress.city,
              country: sudRegAddress.country,
              line1: sudRegAddress.line1,
              line2: sudRegAddress.line2,
              postalCode: sudRegAddress.postalCode,
              state: sudRegAddress.state,
              description: null,
              latitude: null,
              longitude: null,
            }
          : prev.address,
        headquartersAddress: composedAddress || prev.headquartersAddress,
      }));
    },
    []
  );

  const handleAddressSave = useCallback(() => {
    const composedAddress = [
      addressValues.line1,
      addressValues.postalCode,
      addressValues.city,
      addressValues.state,
      addressValues.country,
    ]
      .filter(Boolean)
      .join(", ");

    const structuredAddress: TenantAddress = {
      id: formValues.address?.id ?? null,
      country: addressValues.country,
      state: addressValues.state,
      city: addressValues.city,
      postalCode: addressValues.postalCode,
      line1: addressValues.line1,
      line2: addressValues.line2,
      description: null,
      latitude: addressValues.latitude ?? null,
      longitude: addressValues.longitude ?? null,
    };

    setFormValues((prev) => ({
      ...prev,
      address: structuredAddress,
      headquartersAddress: composedAddress || null,
    }));
    setShowAddressModal(false);
  }, [addressValues, formValues.address]);


  return (
    <Modal show={show} onHide={onClose} size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        onHide={onClose}
        title="Ažuriranje korisnika"
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="h-screen w-full m-auto">
            <div className="flex gap-3 h-full">
              <form
                noValidate
                className="text-left flex-1 "
                onSubmit={handleFormSubmit}
              >
                <div className="p-4">
                  <div className="mb-3">
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      {t("admin.setting.tenant.tenanttype")}
                    </label>
                    <CardSelect
                      options={[
                        { id: "C", icon: "/media/icons/company.svg", title: "Naziv tvrtke", description: "Pravna osoba ili obrt" },
                        { id: "P", icon: "/media/icons/contact.svg", title: "Osoba", description: "Fizička osoba" },
                      ]}
                      value={formValues.tenantTypeId}
                      onChange={handleTenantTypeChange}
                      error={errors.tenantType}
                    />
                  </div>
                  {!isPerson && (
                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="companyName"
                        label={t("admin.setting.tenant.companyname")}
                        placeholder={t(
                          "admin.setting.tenant.companyname.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        value={formValues.companyName}
                        error={errors.companyName}
                        autoComplete="organization"
                        disableDebounce
                        required

                      ></TextField>
                    </div>
                  )}
                  {!isCompany && (
                    <div className="flex gap-4">
                      <div className="mb-3 flex-1">
                        <TextField
                          type="text"
                          name="firstName"
                          label={t("admin.setting.tenant.firstname")}
                          placeholder={t(
                            "admin.setting.tenant.firstname.placeholder"
                          )}
                          onChange={handleTextFieldChange}
                          value={formValues.firstName}
                          error={errors.firstName}
                          autoComplete="given-name"
                          required
                        ></TextField>
                      </div>
                      <div className="mb-3 flex-1">
                        <TextField
                          type="text"
                          name="lastName"
                          label={t("admin.setting.tenant.lastname")}
                          placeholder={t(
                            "admin.setting.tenant.lastname.placeholder"
                          )}
                          onChange={handleTextFieldChange}
                          value={formValues.lastName}
                          error={errors.lastName}
                          autoComplete="family-name"
                          required
                        ></TextField>
                      </div>
                    </div>
                  )}
                  {!isPerson && (
                    <div className="mb-3 flex items-end gap-2">
                      <div className="flex-1">
                        <TextField
                          type="text"
                          name="headquartersAddress"
                          label={t("admin.setting.tenant.headquartersaddress")}
                          placeholder={t(
                            "admin.setting.tenant.headquartersaddress.placeholder"
                          )}
                          onChange={handleTextFieldChange}
                          value={formValues.headquartersAddress}
                          error={errors.headquartersAddress}
                          autoComplete="street-address"
                          onClick={handleOpenAddressModal}
                          readOnly
                          required
                        ></TextField>
                      </div>
                      <Button
                        variant="outlined"
                     
                        onClick={handleOpenAddressModal}
                        startIcon="/media/icons/location.svg"
                      >
                        Karta
                      </Button>
                    </div>
                  )}
                  {/* Mobitel je prvi, a obavezan je barem jedan broj, ne oba. */}
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <MobilePhoneTextField
                        type="tel"
                        name="mobilePhoneNumber"
                        label={t("admin.setting.tenant.mobilephonenumber")}
                        placeholder={t("admin.setting.tenant.mobilephonenumber.placeholder")}
                        onChange={handleTextFieldChange}
                        value={formValues.mobilePhoneNumber}
                        error={errors.mobilePhoneNumber}
                        autoComplete="tel"
                      ></MobilePhoneTextField>
                    </div>
                    <div className="flex-1">
                      <PhoneTextField
                        type="tel"
                        name="phoneNumber"
                        label={t("admin.setting.tenant.phonenumber")}
                        placeholder={t(
                          "admin.setting.tenant.phonenumber.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        value={formValues.phoneNumber}
                        error={errors.phoneNumber}
                        autoComplete="tel"
                      ></PhoneTextField>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Obavezan je barem jedan broj.
                  </p>
                  <div className="flex gap-4 mb-3">
                    <div className="flex-1">
                      <EmailTextField
                        type="email"
                        name="email"
                        label={t("admin.setting.tenant.email")}
                        placeholder={t("admin.setting.tenant.email.placeholder")}
                        onChange={handleTextFieldChange}
                        value={formValues.email}
                        error={errors.email}
                        autoComplete="email"

                      />
                    </div>
                    <div className="flex-1">
                      <WebTextField
                        type="url"
                        name="web"
                        label={t("admin.setting.tenant.web")}
                        placeholder={t("admin.setting.tenant.web.placeholder")}
                        onChange={handleTextFieldChange}
                        value={formValues.web}
                        error={errors.web}
                        autoComplete="url"

                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    {isCompany ? (
                      <OibDropdown
                        name="personalIdentificationNumber"
                        label={t("admin.setting.tenant.personalidentificationnumber")}
                        placeholder={t(
                          "admin.setting.tenant.personalidentificationnumber.placeholder"
                        )}
                        value={formValues.personalIdentificationNumber}
                        error={errors.personalIdentificationNumber}
                        required
                        onSudRegSelect={handleOibSelect}
                      />
                    ) : (
                      <OibTextField
                        type="text"
                        name="personalIdentificationNumber"
                        label={t("admin.setting.tenant.personalidentificationnumber")}
                        placeholder={t(
                          "admin.setting.tenant.personalidentificationnumber.placeholder"
                        )}
                        onChange={handleTextFieldChange}
                        value={formValues.personalIdentificationNumber}
                        error={errors.personalIdentificationNumber}
                        required
                      />
                    )}
                  </div>
                   <div className="mb-3">
                    <Dropdown
                      name="timezone"
                      label={t("admin.setting.tenant.timezone")}
                      placeholder={t("admin.setting.tenant.timezone.placeholder")}
                      options={timezoneOptions}
                      onChange={handleDropdownChange}
                      value={formValues.timezone}
                      error={errors.timezone}
                      required
                    />
                  </div>
                  <div className="mb-3 rounded-sm border border-slate-300 px-3 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Bankovni računi
                      </label>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon="/media/icons/plus.svg"
                        onClick={handleBankAccountAddNew}
                      >
                        Dodaj račun
                      </Button>
                    </div>

                    {bankAccounts.length > 0 ? (
                      <TableSearch
                        data={bankAccounts}
                        columns={TENANT_BANK_ACCOUNT_COLUMNS(handleBankAccountDeleteClick)}
                        selectableRows={false}
                        disablePagination={true}
                        onRowClick={handleBankAccountEdit}
                      />
                    ) : (
                      <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-400">
                        Nema dodanih bankovnih računa
                      </div>
                    )}
                  </div>
                 
                  <div className="mb-3">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      {t("admin.setting.tenant.logo")}
                    </label>
                    <FileUploadControl
                      uploads={logoUpload}
                      onUploadSave={handleLogoUploadSave}
                      onUploadDelete={handleLogoUploadDelete}
                      buttonText="Upload logo"
                      modalTitle="Upload logo"
                      maxFiles={1}
                    />
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={() => onClose()}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    type="submit"
                    disabled={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalBody>

      <TenantBankAccountEditCard

        tenantId={tenantId}
        bankAccountId={editBankAccountId}
        show={showBankAccountModal}
        onSuccess={handleBankAccountEditSuccess}
        onClose={() => setShowBankAccountModal(false)}
      />
      <Dialog
        open={!!deleteBankAccountId}
        title="Brisanje bankovnog računa"
        message="Jeste li sigurni da želite obrisati ovaj bankovni račun?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleBankAccountDeleteConfirm}
        onCancel={() => setDeleteBankAccountId(null)}
      />
      <AddressEditModal
        show={showAddressModal}
        title="Adresa sjedišta tvrtke"
        formValues={addressValues}
        onChange={handleAddressChange}
        onSave={handleAddressSave}
        onClose={handleCloseAddressModal}
      />
    </Modal>
  );
};
export default memo(TenantEditCard);